const CARD_LOGOS = ['visa', 'american-express', 'maestro', 'master-card']

let client
let hf

const useHostedFields = async (fields, handlers) => {
  try {
    await _initBraintree()
    const { hostedFields } = await _loadBraintreeCreditCard()
    const styles = {
      input: { 'font-size': '14px' },
      '.valid': { color: 'green' },
      '.invalid': { color: '#EE5B5B' }
    }
    hf = await hostedFields.create({ client, fields, styles })
    _setupCreditCardHandlers(handlers)
    return hf
  } catch (e) {
    handlers.onError(e)
  }
}

const threeDSecureCheck = async ({
  shippingAddress,
  billingAddress,
  amount,
  email,
  nonce,
  bin,
  ipAddress
}) => {
  await _initBraintree()
  const { threeDSecure } = await _loadBraintreeCreditCard()
  const service = await threeDSecure.create({ version: 2, client })

  const braintreeBillingAddress = {
    givenName: billingAddress.first_name,
    surname: billingAddress.last_name,
    streetAddress:
      `${billingAddress.street_name} ${billingAddress.street_number}`.substring(
        0,
        50
      ),
    extendedAddress: billingAddress?.additional?.substring(0, 50),
    locality: billingAddress.city,
    postalCode: billingAddress.zip,
    countryCodeAlpha2: billingAddress.country.toUpperCase()
  }

  const additionalInformation = {
    shippingGivenName: shippingAddress.first_name,
    shippingSurname: shippingAddress.last_name,
    shippingAddress: {
      streetAddress:
        `${shippingAddress.street_name} ${shippingAddress.street_number}`.substring(
          0,
          50
        ),
      extendedAddress: shippingAddress?.additional?.substring(0, 50),
      locality: shippingAddress.city,
      postalCode: shippingAddress.zip,
      countryCodeAlpha2: shippingAddress.country.toUpperCase()
    }
  }

  // Only include ipAddress if provided (Braintree validation requires it to be a valid IP or omitted)
  if (ipAddress) {
    additionalInformation.ipAddress = ipAddress
  }

  const data = {
    amount,
    nonce,
    bin,
    email,
    billingAddress: braintreeBillingAddress,
    additionalInformation,
    collectDeviceData: true,
    onLookupComplete: (_, next) => {
      next()
    }
  }

  const verification = await service.verifyCard(data)
  const { threeDSecureInfo } = verification

  if (
    !threeDSecureInfo.liabilityShifted &&
    threeDSecureInfo.liabilityShiftPossible
  ) {
    throw new Error('Though card is eligible for 3DS, the check failed')
  }

  return verification
}

const usePaypal = async (handlers) => {
  try {
    await _initBraintree()
    const { dataCollector, payPalCheckout } = await _loadBraintreePaypal()

    // load data collector
    const dataCollectorInstance = await dataCollector.create({ client })
    handlers.dataCollector(dataCollectorInstance.deviceData)
    dataCollectorInstance.teardown()

    // load paypal sdk
    const paypalCheckout = await payPalCheckout.create({ client })
    await paypalCheckout.loadPayPalSDK({ vault: true })

    // direct reference for IDEs, preserve documentation with a twist
    const paypal = window.paypal
    paypal
      .Buttons({
        style: {
          size: 'responsive',
          color: 'gold',
          shape: 'rect',
          label: 'paypal',
          layout: 'horizontal',
          tagline: false,
          disableMaxWidth: true,
          height: 55
        },
        createBillingAgreement() {
          return paypalCheckout.createPayment({ flow: 'vault' })
        },
        async onClick() {
          handlers.onClick()
        },
        async onCancel() {
          handlers.onCancel()
        },
        async onApprove(data) {
          try {
            const { nonce } = await paypalCheckout.tokenizePayment(data)
            handlers.onApprove(nonce)
          } catch (error) {
            handlers.onError(error)
          }
        },
        onError(error) {
          handlers.onError(error)
        }
      })
      .render('#paypal-button')
  } catch (error) {
    handlers.onError(error)
  }
}

const _setupCreditCardHandlers = (handlers) => {
  hf.on('focus', ({ fields, emittedBy }) => {
    const { container } = fields[emittedBy]
    _displayMessage(container, false)
  })

  hf.on('blur', ({ fields, emittedBy }) => {
    const { isValid, container } = fields[emittedBy]

    if (!isValid) {
      _displayMessage(container, true)
      return
    }
    _displayMessage(container, false)
  })

  hf.on('validityChange', async ({ fields }) => {
    const oneOrMoreInvalid = Object.values(fields).some(
      ({ isValid }) => !isValid
    )
    if (oneOrMoreInvalid) {
      handlers.validityChange({ cardValid: false })
      return
    }

    const data = await hf.tokenize()
    handlers.validityChange({ ...data, cardValid: true })
  })

  hf.on('cardTypeChange', ({ cards }) => {
    if (cards.length !== 1) {
      handlers.cardTypeChange(null)
      return
    }
    const type = cards[0].type
    if (!~CARD_LOGOS.indexOf(type)) {
      handlers.cardTypeChange(null)
      return
    }
    handlers.cardTypeChange(type)
  })
}

const BRAINTREE_CLIENT_TOKEN_KEY = 'braintree_client_token'

const _initBraintree = async () => {
  if (client) return client

  const { default: braintree } = await import('braintree-web/client')

  // Reuse cached token to keep nonces valid across page reloads
  let token =
    typeof sessionStorage !== 'undefined' &&
    sessionStorage.getItem(BRAINTREE_CLIENT_TOKEN_KEY)

  if (!token) {
    const { customerStore } = await _getModules()
    await customerStore.waitForToken()
    token = customerStore.customerPaymentToken
  }

  try {
    client = await braintree.create({ authorization: token })
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(BRAINTREE_CLIENT_TOKEN_KEY, token)
    }
  } catch (e) {
    // Cached token may be expired; retry with a fresh one
    if (e?.message) {
      console.warn(
        'Braintree cached token invalid, fetching fresh token:',
        e.message
      )
    }
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(BRAINTREE_CLIENT_TOKEN_KEY)
    }
    const { customerStore } = await _getModules()
    await customerStore.waitForToken()
    token = customerStore.customerPaymentToken
    client = await braintree.create({ authorization: token })
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(BRAINTREE_CLIENT_TOKEN_KEY, token)
    }
  }

  return client
}

const clearBraintreeClient = () => {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(BRAINTREE_CLIENT_TOKEN_KEY)
  }
  client = null
}

const _displayMessage = ({ id }, value) => {
  const errorElement = document.getElementById(`${id}-error`)

  if (!errorElement) return

  errorElement.dataset.visible = '' + value
}

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const { useCustomerStore } = await import('@/stores/customer')
    cache = { customerStore: useCustomerStore() }
    return cache
  }
})()

const _loadBraintreePaypal = async () => {
  const [{ default: dataCollector }, { default: payPalCheckout }] =
    await Promise.all([
      import('braintree-web/data-collector'),
      import('braintree-web/paypal-checkout')
    ])
  return { dataCollector, payPalCheckout }
}

const _loadBraintreeCreditCard = async () => {
  const [{ default: hostedFields }, { default: threeDSecure }] =
    await Promise.all([
      import('braintree-web/hosted-fields'),
      import('braintree-web/three-d-secure')
    ])
  return { hostedFields, threeDSecure }
}

export {
  _initBraintree,
  clearBraintreeClient,
  client,
  threeDSecureCheck,
  useHostedFields,
  usePaypal
}
